import { useEffect, useMemo, useRef, useState } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import FlexColumn from "../FlexBox/FlexColumn";
import FlexRow from "../FlexBox/FlexRow";
import Typography from "../Typography/Typography";
import TableFilters from "./TableFilters";
import { Checkbox } from "@material-ui/core";
import './mobileTableStyles.scss';
import SButton from "../SButton/SButton";

const MobileTable = ({
    columns,
    data,
    filtersConfig,
    filters,
    setFilters,
    actions = [],
    selection = {},
    pagination,
    changePage,
    emptyMessage = "No data found", // Added emptyMessage prop with default value
    onCardClick
}) => {

    const parentRef = useRef(null);
    const [selected, setSelected] = useState({});
    const [showMore, setShowMore] = useState(false);
    const [lastFetchedPage, setLastFetchedPage] = useState(pagination?.currentPage);

    const isFetchingRef = useRef(false);
    const lastTriggerIndexRef = useRef(-1);

    const rowVirtualizer = useVirtualizer({
        count: data?.length || 0,
        getScrollElement: () => parentRef.current,
        estimateSize: () => 120,
        overscan: 5,
    });

    const hSelection = (d) => {
        const nSel = { ...selected };

        if (nSel[d.id]) {
            delete nSel[d.id];
        } else {
            nSel[d.id] = d;
        }

        setSelected(nSel);
        selection?.onSelectionChange &&
            selection?.onSelectionChange(
                Object.entries(nSel).map(([, item]) => item)
            );
    };

    const selectedLen = useMemo(() => {
        let length = 0;
        if (selection?.enabled) {
            length = Object.entries(selected).length;
            length &&
                selection?.notSelect?.forEach((id) => {
                    const found = data.find((d) => d.id === id);
                    if (found) {
                        length++;
                    }
                });
        }

        return length;
    }, [selection, selected, data]);

    const hSelectionAll = () => {
        if (selectedLen !== data.length) {
            const aSel = data.reduce((obj, arr) => {
                if (!selection?.notSelect?.includes(arr.id)) {
                    obj[arr.id] = arr;
                }
                return obj;
            }, {});
            setSelected(aSel);
            selection?.onSelectionChange(Object.entries(aSel).map(([, item]) => item));
        } else {
            setSelected({});
            selection?.onSelectionChange([]);
        }
    };

    useEffect(() => {
        if (pagination?.currentPage) {
            setLastFetchedPage(pagination.currentPage);
            isFetchingRef.current = false; // 🔓 Unlock fetch on API success
        }
    }, [pagination?.currentPage]);


    const virtualItems = rowVirtualizer.getVirtualItems();
    const lastRenderedIndex =
        virtualItems.length > 0
            ? virtualItems[virtualItems.length - 1].index
            : -1;

    useEffect(() => {
        if (!pagination || !changePage || !data?.length) return;

        const isAtBottom = lastRenderedIndex >= data.length - 3;
        if (!isAtBottom) return;

        if (lastTriggerIndexRef.current === lastRenderedIndex) return;

        if (isFetchingRef.current) return;

        if (!pagination.showNextButton) return;

        if (pagination.currentPage !== lastFetchedPage) return;

        isFetchingRef.current = true;
        lastTriggerIndexRef.current = lastRenderedIndex;

        const nextPage = pagination.currentPage + 1;
        changePage(nextPage);
        setLastFetchedPage(nextPage);

    }, [lastRenderedIndex, data?.length, pagination, changePage, lastFetchedPage]);

    // Check if there is no data to display
    const isEmpty = !data || data.length === 0;

    return (
        <FlexColumn flexWrap="nowrap" styles={{ height: '83vh' }}>
            <FlexColumn
                alignItems="end"
                styles={{ paddingBottom: filtersConfig?.length > 1 ? '0.2rem' : '1rem' }}
                className="mobile-table-filters-container"
            >
                {filtersConfig
                    ?.slice(0, showMore ? filtersConfig?.length : 1)
                    ?.map((fc) => (
                        <TableFilters
                            key={fc?.key} // Added key for React list rendering best practices
                            filterConfig={fc}
                            value={filters[fc?.key] || ''}
                            setFilters={setFilters}
                        />
                    ))}

                {filtersConfig?.length > 1 && (
                    <SButton
                        variant={"tertiary"}
                        style={{ fontSize: '0.6rem', marginTop: '-0.5rem' }}
                        onClick={() => {
                            setShowMore(!showMore);
                        }}
                    >
                        Show {showMore ? "Less" : "More"}
                    </SButton>
                )}
            </FlexColumn>

            {selection?.enabled && <FlexRow gap="0.5rem" justifyContent="start" styles={{ marginLeft: '1.1rem', opacity: selection?.notSelect?.length === data?.length ? '0.4' : '1' }}>
                <Checkbox
                    style={{ padding: '0' }}
                    checked={selectedLen > 0 && selectedLen === data.length}
                    disabled={selection?.notSelect?.length === data?.length}
                    onChange={() => { hSelectionAll() }}
                />
                <Typography variant="b2M" color="var(--grey-500)">Select All</Typography>
            </FlexRow>}

            <div
                ref={parentRef}
                className="hide-scrollbar"
                style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    width: "100%",
                    borderTop: '2px solid var(--grey-100)'
                }}
            >
                {isEmpty ? (
                    // Render Empty State
                    <FlexColumn
                        alignItems="center"
                        justifyContent="center"
                        styles={{ height: '50%', padding: '2rem' }}
                    >
                        <Typography variant={"b2R"} color="var(--grey-600)">
                            {emptyMessage}
                        </Typography>
                    </FlexColumn>
                ) : (
                    // Render Virtualized List
                    <div
                        style={{
                            height: `${rowVirtualizer.getTotalSize()}px`,
                            width: "100%",
                            position: "relative"
                        }}
                    >
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                            const d = data[virtualRow.index];
                            return (
                                <div
                                    key={virtualRow.key}
                                    ref={rowVirtualizer.measureElement}
                                    data-index={virtualRow.index}
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        width: "100%",
                                        transform: `translateY(${virtualRow.start}px)`,
                                        paddingBottom: "1rem",
                                        paddingTop: '0.5rem'
                                    }}
                                >
                                    <FlexRow
                                        flexWrap="nowrap"
                                        gap="0.5rem"
                                        styles={{
                                            border: '1px solid var(--grey-300)',
                                            borderRadius: '0.5rem',
                                            padding: '0.875rem',
                                            paddingLeft: selection?.enabled ? "0.5rem" : '0.875rem',
                                            background: 'var(--state-w1)'
                                        }}
                                        alignItems="start"
                                    >
                                        {selection?.enabled && <Checkbox
                                            style={{ padding: '0', opacity: selection?.notSelect?.includes(d?.id) ? '0.4' : '1' }}
                                            checked={!!selected[d?.id]}
                                            disabled={selection?.notSelect?.includes(d?.id)}
                                            onChange={() => hSelection(d)}
                                        />}
                                        <FlexColumn gap="0.5rem">
                                            <FlexColumn gap="0.5rem" onClick={() => { onCardClick && onCardClick(d) }}>
                                                {columns?.map((c, c_index) => (
                                                    <FlexRow
                                                        key={c?.name || c_index}
                                                        justifyContent="space-between"
                                                        flexWrap="nowrap"
                                                    >
                                                        <Typography style={{ whiteSpace: 'nowrap' }} variant={"b3R"} color="var(--grey-700)">
                                                            {c?.title}
                                                        </Typography>

                                                        {c?.renderM
                                                            ? c.renderM(c, d)
                                                            : c?.render
                                                                ? c.render(c, d)
                                                                : (
                                                                    <Typography style={{ textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '75%' }} variant={"b3R"}>
                                                                        {d[c?.name] || "-"}
                                                                    </Typography>
                                                                )}
                                                    </FlexRow>
                                                ))}
                                            </FlexColumn>

                                            <FlexRow justifyContent="end">
                                                {actions?.map((a, a_index) =>
                                                    a?.renderM(a, d, virtualRow?.index)
                                                )}
                                            </FlexRow>
                                        </FlexColumn>

                                    </FlexRow>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </FlexColumn>
    );
};

export default MobileTable;